import { styled } from "styled-components";

import { Row } from "../../styles/layout";
import { SmallText } from "../../styles/text";

import cardImage from "./assets/card.svg";
import paypalImage from "./assets/paypal.svg";

type SwitchValues = "card" | "paypal";

const SelectorContainer = styled(Row)(
  ({ theme }) => `
  width: 100%;
  background: rgba(0, 0, 0, 0.5);
  border: 1px solid ${theme.colors.grayMedium};
  border-radius: 30px;
  max-width: 345px;
  `,
);

const SelectorButton = styled.button<{ $selected: boolean }>(
  ({ theme, $selected }) => `
  flex: 1;
  gap: 5px;
  padding: 0;
  height: 44px;

  background: ${$selected ? theme.colors.blueLight : "unset"};
  border: none;
  border-radius: 25%/100%;

  display: flex;
  justify-content: center;
  align-items: center;

  & > img {
    height: 50%;
  }
`,
);

const PayPalLogo = styled.div<{ $invert: 0 | 1 }>(
  ({ $invert }) => `
  display: flex;
  justify-content: center;
  filter: invert(${$invert})
`,
);

const CreditCardLogo = styled.div<{ $color: "grayLight" | "blueDarkest" }>(
  ({ $color }) => `
  display: flex;
  justify-content: center;
  filter: ${
    $color === "blueDarkest"
      ? "invert(7%) sepia(63%) saturate(1567%) hue-rotate(194deg) brightness(106%) contrast(95%);"
      : "invert(46%) sepia(4%) saturate(596%) hue-rotate(184deg) brightness(97%) contrast(90%);"
  }
  `,
);

export interface PaymentMethodSelectorProps {
  value: SwitchValues;
  onChange: (selection: SwitchValues) => void;
}

export const PaymentMethodSelector = ({
  value,
  onChange,
}: PaymentMethodSelectorProps) => {
  // Trigger onChange only when value changes
  const clickHandler = (sel: SwitchValues) => () => {
    if (value !== sel) {
      onChange(sel);
    }
  };

  const CenteredCreditCard =
    value === "card" ? (
      <>
        <CreditCardLogo $color="blueDarkest">
          <img src={cardImage} alt="" />
        </CreditCardLogo>
        <SmallText $fontWeight="bold" $color="blueDarkest">
          Credit Card
        </SmallText>
      </>
    ) : (
      <>
        <CreditCardLogo $color="grayLight">
          <img src={cardImage} alt="" />
        </CreditCardLogo>
        <SmallText $fontWeight="bold" $color="grayLight">
          Credit Card
        </SmallText>
      </>
    );

  const PayPal =
    value === "paypal" ? (
      <PayPalLogo $invert={1}>
        <img src={paypalImage} alt="PayPal" />
      </PayPalLogo>
    ) : (
      <PayPalLogo $invert={0}>
        <img src={paypalImage} alt="PayPal" />
      </PayPalLogo>
    );

  return (
    <SelectorContainer>
      <SelectorButton
        $selected={value === "card"}
        type="button"
        onClick={clickHandler("card")}
        data-testid="credit-card-selector-button"
      >
        {CenteredCreditCard}
      </SelectorButton>
      <SelectorButton
        $selected={value === "paypal"}
        type="button"
        onClick={clickHandler("paypal")}
        data-testid="paypal-selector-button"
      >
        {PayPal}
      </SelectorButton>
    </SelectorContainer>
  );
};
